export type BootLogLevel = 'info' | 'warn' | 'error'
export type BootChunkStatus = 'loading' | 'loaded' | 'failed'

export interface BootLogEntry {
  at: string
  detail?: string
  level: BootLogLevel
  stage: string
}

export interface BootChunkEntry {
  detail?: string
  name: string
  status: BootChunkStatus
  updatedAt: string
}

export interface BootSnapshot {
  chunks: Record<string, BootChunkEntry>
  env: {
    esModules: boolean
    screen: string
    serviceWorker: boolean
    ua: string
  }
  errors: BootLogEntry[]
  logs: BootLogEntry[]
  mounted: boolean
  stage: string
  startedAt: number
  version?: string
}

declare global {
  interface Window {
    __APP_BOOT__?: Partial<BootSnapshot> & {
      markMounted?: () => void
      render?: () => void
      setChunk?: (name: string, status: BootChunkStatus, detail?: string) => void
      setStage?: (stage: string, detail?: string) => void
    }
  }
}

function nowIso() {
  return new Date().toISOString()
}

function getDefaultSnapshot(): BootSnapshot {
  if (typeof window === 'undefined') {
    return {
      chunks: {},
      env: { esModules: true, screen: 'server', serviceWorker: false, ua: 'server' },
      errors: [],
      logs: [],
      mounted: false,
      stage: 'Servidor',
      startedAt: Date.now(),
    }
  }

  const script = document.createElement('script')
  const esModules = Boolean(
    (typeof HTMLScriptElement !== 'undefined' && 'supports' in HTMLScriptElement && HTMLScriptElement.supports?.('module')) ||
      'noModule' in script,
  )

  return {
    chunks: {},
    env: {
      esModules,
      screen: `${window.innerWidth}×${window.innerHeight} @${window.devicePixelRatio || 1}`,
      serviceWorker: 'serviceWorker' in navigator,
      ua: navigator.userAgent,
    },
    errors: [],
    logs: [],
    mounted: false,
    stage: 'Iniciando...',
    startedAt: Date.now(),
  }
}

function emitUpdate() {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent('app-boot-update'))
}

function ensureState() {
  if (typeof window === 'undefined') return null
  if (!window.__APP_BOOT__) {
    window.__APP_BOOT__ = getDefaultSnapshot()
  }
  return window.__APP_BOOT__
}

function appendLog(level: BootLogLevel, stage: string, detail?: string) {
  const state = ensureState()
  if (!state) return

  const entry: BootLogEntry = {
    at: nowIso(),
    detail,
    level,
    stage,
  }

  const nextLogs = [...(state.logs || []), entry].slice(-100)
  state.logs = nextLogs
  if (level === 'error') {
    state.errors = [...(state.errors || []), entry].slice(-100)
  }
  state.render?.()
  emitUpdate()
}

export function bootSetStage(stage: string, detail?: string) {
  const state = ensureState()
  if (!state) return
  if (typeof state.setStage === 'function') {
    state.setStage(stage, detail)
    return
  }
  state.stage = stage
  appendLog('info', stage, detail)
}

export function bootSetChunk(name: string, status: BootChunkStatus, detail?: string) {
  const state = ensureState()
  if (!state) return
  if (typeof state.setChunk === 'function') {
    state.setChunk(name, status, detail)
    return
  }
  state.chunks = {
    ...(state.chunks || {}),
    [name]: {
      detail,
      name,
      status,
      updatedAt: nowIso(),
    },
  }
  appendLog(status === 'failed' ? 'error' : 'info', `Chunk ${name}`, `${status}${detail ? ` — ${detail}` : ''}`)
}

export function bootError(stage: string, detail?: string) {
  appendLog('error', stage, detail)
}

export function bootMarkMounted() {
  const state = ensureState()
  if (!state) return
  if (typeof state.markMounted === 'function') {
    state.markMounted()
    return
  }
  state.mounted = true
  state.stage = 'React montado'
  appendLog('info', 'React montado')
}

export function getBootSnapshot(): BootSnapshot {
  const state = ensureState()
  if (!state) return getDefaultSnapshot()
  return {
    ...getDefaultSnapshot(),
    ...state,
    chunks: { ...(state.chunks || {}) },
    env: {
      ...getDefaultSnapshot().env,
      ...(state.env || {}),
    },
    errors: [...(state.errors || [])],
    logs: [...(state.logs || [])],
    mounted: Boolean(state.mounted),
    stage: state.stage || 'Iniciando...',
    startedAt: state.startedAt || Date.now(),
  }
}

export async function hardReloadApp() {
  bootSetStage('Limpando cache...', 'Forçando atualização completa')

  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registrations.map((registration) => registration.unregister()))
    }
  } catch (error) {
    bootError('Falha ao remover service worker', error instanceof Error ? error.message : String(error))
  }

  try {
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map((key) => caches.delete(key)))
    }
  } catch (error) {
    bootError('Falha ao limpar cache', error instanceof Error ? error.message : String(error))
  }

  const url = new URL(window.location.href)
  url.searchParams.set('reload', String(Date.now()))
  window.location.replace(url.toString())
}